import { ComponentType, Suspense, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Surface } from '@vst/beam';
import { useTranslation } from 'react-i18next';

import { ShopAddonsLink } from '@app/router/constants';
import CollapsibleCart from '@app/views/ShopAddons/components/AddonCollapsibleCart';
import { useNavigate, useScreenResolution } from '@app/hooks';
import { scrollToTop } from '@app/redux/utils';

import { HeaderWithBackNavigation } from '../HeaderWithBackNavigation';
import { StepCounter } from '../StepCounter';

import styles from './styles.module.scss';

type StepComponentProps = {
  continueButtonLabel: string;
  handleContinue: () => void;
  handleBack: () => void;
  isDesktop?: boolean;
  isLastStep?: boolean;
};

export type Step = {
  title: string;
  component: ComponentType<StepComponentProps>;
  buttonLabel?: string;
  action?: () => void;
  current?: boolean;
  displayCollapsibleCart?: boolean;
  displayTC?: boolean;
};

type StepNavigationProps = {
  steps: Step[];
  nameSpace: string;
};

const StepNavigation = ({ steps, nameSpace }: StepNavigationProps) => {
  const { t } = useTranslation(nameSpace);
  const dispatch = useDispatch();
  const { replace } = useNavigate();
  const { isMedium, isLarge, isExtraLarge } = useScreenResolution();
  const isDesktop = isLarge || isExtraLarge;

  const [stepList, setStepList] = useState(steps);
  const [currentIndex, setCurrentIndex] = useState(steps.findIndex((step) => step.current) || 0);

  const handleBack = () => {
    if (currentIndex === 0) {
      window.history.back();
    } else {
      goToStep(currentIndex - 1);
    }
  };

  const handleContinue = () => {
    const currentStep = steps[currentIndex];

    // If defined, the function defined in action is executed and its result is saved in shouldContinue
    const shouldContinue = currentStep.action?.() ?? true;
    if (!shouldContinue) {
      return;
    }

    const isLastStep = currentIndex === stepList.length - 1;
    if (!isLastStep) {
      goToStep(currentIndex + 1);
    }
  };

  const goToStep = (index: number) => {
    const updatedSteps = stepList.map(
      (step, idx) =>
        ({
          ...step,
          current: idx === index,
        } as Step)
    );
    setStepList(updatedSteps);
    setCurrentIndex(index);
  };

  useEffect(() => {
    dispatch(scrollToTop());
  }, [currentIndex]);

  useEffect(() => {
    // Update stepList but preserve currentIndex
    setStepList(() =>
      steps.map((step, idx) => ({
        ...step,
        current: idx === currentIndex,
      }))
    );
  }, [steps]);

  const CurrentStepComponent = stepList[currentIndex].component;
  const currentStep = stepList[currentIndex];
  const isLastStep = currentIndex === stepList.length - 1;
  const continueButtonLabel = currentStep.buttonLabel || (isLastStep ? `${t('subscribeNow')}` : `${t('continue')}`);

  return (
    <>
      {currentStep.displayCollapsibleCart && !isDesktop && (
        <CollapsibleCart
          submitButtonLabel={continueButtonLabel}
          handleClick={handleContinue}
          displayTC={currentStep.displayTC ?? true}
          isLastStep={isLastStep}
        />
      )}
      <div className={styles['wrapper']}>
        <HeaderWithBackNavigation
          handleBackNavigation={() => replace(`/${ShopAddonsLink.ShopAddons}`)}
          title={currentStep.title}
          goBackLabel={t('allAddOns')}
          additionalClassName={styles['header']}
        />
        <Surface variant={isDesktop || isMedium ? 'secondary' : 'primary'} mb={isDesktop || isMedium ? '64px' : '8px'}>
          <StepCounter step={currentIndex + 1} maxSteps={stepList.length} />
        </Surface>
        <Suspense fallback={<div>Loading...</div>}>
          {CurrentStepComponent && (
            <CurrentStepComponent
              {...currentStep}
              continueButtonLabel={continueButtonLabel}
              handleContinue={handleContinue}
              handleBack={handleBack}
              isDesktop={isDesktop}
              isLastStep={isLastStep}
            />
          )}
        </Suspense>
      </div>
    </>
  );
};

export default StepNavigation;
